import { useTranslation } from 'react-i18next'
import { Building2, Folder, Lock } from 'lucide-react'
import type { SavedFilterPermission } from '@/lib/types/savedFilter'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface SavedFilterPermissionPickerProps {
  value: SavedFilterPermission
  onChange: (value: SavedFilterPermission) => void
  disabled?: boolean
  /** The project or space the view lives in; names the middle option. */
  placeName?: string | null
}

/** Who can see a view: Only me / [place] / Workspace (design 20b wording). */
export function SavedFilterPermissionPicker({
  value,
  onChange,
  disabled,
  placeName,
}: SavedFilterPermissionPickerProps) {
  const { t } = useTranslation('tasks')
  const options: Array<{
    value: SavedFilterPermission
    label: string
    description: string
    icon: React.ComponentType<{ className?: string }>
  }> = [
    {
      value: 'private',
      label: t('savedFilters.visibility.private'),
      description: t('savedFilters.visibility.privateHint'),
      icon: Lock,
    },
    {
      value: 'project',
      label: placeName ?? t('savedFilters.visibility.project'),
      description: placeName
        ? t('savedFilters.visibility.projectHint', { place: placeName })
        : t('savedFilters.visibility.projectHintGeneric'),
      icon: Folder,
    },
    {
      value: 'workspace',
      label: t('savedFilters.visibility.workspace'),
      description: t('savedFilters.visibility.workspaceHint'),
      icon: Building2,
    },
  ]

  return (
    <Select value={value} onValueChange={(v) => onChange(v as SavedFilterPermission)} disabled={disabled}>
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            <div className="flex items-center gap-2">
              <opt.icon className="size-4 shrink-0 text-muted-foreground" />
              <div className="text-left">
                <div className="text-sm">{opt.label}</div>
                <div className="text-xs text-muted-foreground">{opt.description}</div>
              </div>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
